import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import styles from './ProfileActionsMenu.module.scss';

// ============================================
// TYPES
// ============================================

export interface ProfileAction {
  id: string;
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  /** Visually highlight as a primary/accent action */
  variant?: 'default' | 'primary';
}

interface ProfileActionsMenuProps {
  actions: ProfileAction[];
}

// ============================================
// ICONS
// ============================================

function MoreHorizontalIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="5" cy="12" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

// ============================================
// COMPONENT
// ============================================

const ESTIMATED_ITEM_HEIGHT = 40;
const MENU_VERTICAL_PADDING = 16;
const FLIP_GAP = 4;

function ProfileActionsMenu({ actions }: ProfileActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    placement: 'top' | 'bottom';
  }>({ top: 0, left: 0, placement: 'bottom' });

  const toggle = useCallback(() => setOpen((prev) => !prev), []);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const estimatedHeight = actions.length * ESTIMATED_ITEM_HEIGHT + MENU_VERTICAL_PADDING;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldFlip =
      spaceBelow < estimatedHeight + FLIP_GAP && spaceAbove > spaceBelow;
    setPosition({
      top: shouldFlip ? rect.top - FLIP_GAP : rect.bottom + FLIP_GAP,
      left: rect.right,
      placement: shouldFlip ? 'top' : 'bottom',
    });
  }, [open, actions.length]);

  // Close on outside click or escape
  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    // Close on scroll (parent containers may shift position)
    const handleScroll = () => setOpen(false);

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  if (actions.length === 0) return null;

  return (
    <div className={styles.wrapper}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={toggle}
        aria-label="Actions"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <MoreHorizontalIcon />
      </button>
      {open && createPortal(
        <div
          ref={dropdownRef}
          className={styles.dropdown}
          role="menu"
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            transform:
              position.placement === 'top'
                ? 'translate(-100%, -100%)'
                : 'translateX(-100%)',
          }}
        >
          {actions.map((action) => (
            <button
              key={action.id}
              type="button"
              role="menuitem"
              className={`${styles.menuItem} ${action.variant === 'primary' ? styles.menuItemPrimary : ''}`}
              onClick={() => {
                setOpen(false);
                action.onClick();
              }}
            >
              {action.icon && <span className={styles.menuItemIcon}>{action.icon}</span>}
              <span>{action.label}</span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

export default ProfileActionsMenu;
